import React, { forwardRef, ReactNode } from 'react';
import {
    OutlinedInput,
    OutlinedInputProps,
    outlinedInputClasses,
    inputLabelClasses,
    InputLabel,
    FormControl,
    AutocompleteRenderInputParams,
} from '@mui/material';

import { Typography } from '../Typography';


export type SearchProps = Pick<
OutlinedInputProps,
| 'value'
| 'onChange'
| 'onFocus'
| 'onBlur'
| 'onKeyUp'
| 'onKeyDown'
| 'placeholder'
| 'sx'
| 'fullWidth'
| 'inputProps'
| 'label'
> & {
    startIcon?: ReactNode;
    isGray: boolean;
    onClear?: () => void;
    isFocused: boolean;
    onClick: () => void;
};


export const Search2 = forwardRef<HTMLInputElement, SearchProps & Pick<AutocompleteRenderInputParams, 'InputLabelProps'>>(
    ({
        sx = [],
        isFocused,
        isGray,
        onClear,
        InputLabelProps,
        label,
        startIcon,
        ...props
    }, ref) => (
        <FormControl
            fullWidth
            variant='standard'
            sx={{
                overflow: 'hidden',
                '&:hover': {
                    [`& .${inputLabelClasses.root}`]: {
                        borderRadius: 3,
                        bgColor: 'transparent',
                    },
                    [`& .${outlinedInputClasses.root}`]: {
                        bgcolor: isGray
                            ? 'grayscale.shade20'
                            : 'grayscale.shade0',
                    },
                },
            }}
        >
            {label && (
                <InputLabel
                    {...InputLabelProps}
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        top: isFocused || props.value ? -6 : -20,
                        zIndex: 1,
                        pl: isFocused || props.value ? 5 : 4,
                    }}
                >
                    <Typography color={isFocused ? 'general.secondary50' : 'grayscale.shade85'} variant='regularBody2'>{label}</Typography>
                </InputLabel>
            )}
            <OutlinedInput
                {...props}
                ref={ref}
                startAdornment={startIcon}
                sx={[
                    {
                        typography: 'regularBody2',
                        borderRadius: 3,
                        bgcolor: isGray ? 'grayscale.shade10' : 'grayscale.shade0',
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            border: 1,
                            borderColor: 'transparent',
                        },
                        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: isGray
                                ? 'transparent'
                                : 'grayscale.shade40',
                        },
                        '&:hover': {
                            bgcolor: isGray
                                ? 'grayscale.shade20'
                                : 'grayscale.shade0',
                        },
                        [`&.${outlinedInputClasses.focused}`]: {
                            bgcolor: isGray
                                ? 'grayscale.shade10'
                                : 'grayscale.shade0',
                        },
                        [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
                        {
                            borderColor: 'grayscale.shade60',
                            borderWidth: 1,
                        },
                    },
                    ...Array.isArray(sx) ? sx : [sx],
                ]}
            />
        </FormControl>
    ),
);
